/**
 * The one way the web app talks to the backend.
 *
 * - Sends the session cookie (same origin) and the CSRF header the backend requires.
 * - Turns every failure into an ApiError carrying the status, so callers can tell
 *   "sign in again" (401), "never going to work" (403), "someone else changed it" (409),
 *   "fix the form" (422) and "try again" (0 / 5xx) apart.
 */

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId: string | undefined;

  constructor(status: number, code: string, message: string, details?: unknown, requestId?: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }

  get isRetryable(): boolean {
    return this.status === 0 || this.status >= 500;
  }
}

export interface FieldError {
  field: string;
  message: string;
}

export function fieldErrors(error: unknown): FieldError[] {
  if (error instanceof ApiError && error.status === 422 && Array.isArray(error.details)) {
    return error.details as FieldError[];
  }
  return [];
}

type Method = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface Options {
  method?: Method;
  body?: unknown;
  signal?: AbortSignal;
}

export const CSRF_HEADER = { name: "X-Requested-With", value: "support-desk" } as const;

const NETWORK_MESSAGE = "Can't reach the support desk. Check your connection and try again.";

export async function api<T>(path: string, options: Options = {}): Promise<T> {
  const { method = "GET", body, signal } = options;
  const headers: Record<string, string> = { Accept: "application/json", [CSRF_HEADER.name]: CSRF_HEADER.value };
  const init: RequestInit = { method, headers, credentials: "same-origin" };
  if (body instanceof FormData) {
    init.body = body; // file uploads: the browser sets the multipart boundary itself
  } else if (body !== undefined) {
    headers["Content-Type"] = "application/json";
    init.body = JSON.stringify(body);
  }
  if (signal) {
    init.signal = signal;
  }

  let response: Response;
  try {
    response = await fetch(path, init);
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new ApiError(0, "network_error", NETWORK_MESSAGE);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (data as { error?: { code?: string; message?: string; details?: unknown; request_id?: string } } | null)
      ?.error;
    throw new ApiError(
      response.status,
      error?.code ?? "http_error",
      error?.message ?? "Something went wrong. Please try again.",
      error?.details,
      error?.request_id,
    );
  }
  return data as T;
}
